import { User } from 'firebase/auth';
import { refreshAppCheckToken } from './firebaseService';
import { refreshFirebaseAuthToken } from '../utils/tokenUtils';

/**
 * Service to coordinate Firebase App Check and Authentication token refreshes
 * Prevents cascade failures when tokens expire during long-running sessions
 */
export class TokenCoordinationService {
  private refreshInProgress = false;
  private lastRefreshAttempt = 0;
  private readonly MIN_REFRESH_INTERVAL = 5 * 60 * 1000; // 5 minutes
  private readonly MAX_RETRY_ATTEMPTS = 3;

  /**
   * Performs coordinated refresh of both App Check and Auth tokens
   * @param user Current Firebase user
   * @returns Promise<boolean> Success status
   */
  async coordinatedTokenRefresh(user: User | null): Promise<boolean> {
    if (this.refreshInProgress) {
      console.log('[TokenCoordination] Refresh already in progress');
      return false;
    }

    const now = Date.now();
    if (now - this.lastRefreshAttempt < this.MIN_REFRESH_INTERVAL) {
      console.log('[TokenCoordination] Too soon since last refresh attempt');
      return false;
    }

    this.refreshInProgress = true;
    this.lastRefreshAttempt = now;

    try {
      console.log('[TokenCoordination] Starting coordinated token refresh');
      
      // Refresh App Check token first - this is critical for Firestore access
      const appCheckSuccess = await this.refreshAppCheckWithRetry();
      console.log('[TokenCoordination] App Check refresh:', appCheckSuccess ? 'success' : 'failed');
      
      // Then refresh auth token to get latest custom claims
      let authSuccess = false;
      if (user) {
        const authToken = await refreshFirebaseAuthToken(user);
        authSuccess = !!authToken;
        console.log('[TokenCoordination] Auth token refresh:', authSuccess ? 'success' : 'failed');
      }

      const overallSuccess = appCheckSuccess && authSuccess;
      console.log('[TokenCoordination] Coordinated refresh result:', overallSuccess ? 'SUCCESS' : 'FAILED');
      
      return overallSuccess;
    } catch (error) {
      console.error('[TokenCoordination] Coordinated refresh failed:', error);
      return false;
    } finally {
      this.refreshInProgress = false;
    }
  }

  /**
   * Refresh App Check token with retry logic and exponential backoff
   */
  private async refreshAppCheckWithRetry(): Promise<boolean> {
    for (let attempt = 1; attempt <= this.MAX_RETRY_ATTEMPTS; attempt++) {
      try {
        const success = await refreshAppCheckToken();
        if (success) {
          return true;
        }
        
        // If not successful, wait before retrying (exponential backoff)
        if (attempt < this.MAX_RETRY_ATTEMPTS) {
          const delay = Math.min(1000 * Math.pow(2, attempt - 1), 10000); // Cap at 10 seconds
          console.log(`[TokenCoordination] App Check retry ${attempt} failed, waiting ${delay}ms`);
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      } catch (error: any) {
        console.error(`[TokenCoordination] App Check attempt ${attempt} error:`, error);
        
        // If throttled, don't retry immediately
        if (error.code === 'appCheck/throttled') {
          console.error('[TokenCoordination] App Check throttled, aborting retry attempts');
          return false;
        }
        
        // For other errors, continue with exponential backoff
        if (attempt < this.MAX_RETRY_ATTEMPTS) {
          const delay = Math.min(2000 * Math.pow(2, attempt - 1), 15000); // Cap at 15 seconds
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
    }
    
    return false;
  }

  /**
   * Check if a token refresh is needed based on expiration time
   * @param user Current Firebase user
   * @returns Promise<boolean> True if refresh is recommended
   */
  async shouldRefreshTokens(user: User | null): Promise<boolean> {
    if (!user) return false;

    try {
      const tokenResult = await user.getIdTokenResult();
      const expirationTime = new Date(tokenResult.expirationTime).getTime();
      const now = Date.now();
      const timeUntilExpiry = expirationTime - now;
      
      // Refresh if token expires within 5 minutes
      const REFRESH_THRESHOLD = 5 * 60 * 1000;
      return timeUntilExpiry <= REFRESH_THRESHOLD;
    } catch (error) {
      console.error('[TokenCoordination] Error checking token expiry:', error);
      return true; // Assume refresh needed if we can't check
    }
  }

  /**
   * Get current refresh status
   */
  getRefreshStatus() {
    return {
      refreshInProgress: this.refreshInProgress,
      lastRefreshAttempt: this.lastRefreshAttempt,
      timeSinceLastRefresh: Date.now() - this.lastRefreshAttempt
    };
  }
}

// Export singleton instance
export const tokenCoordinator = new TokenCoordinationService();
